// src/components/Galerie.tsx
import PanoramaViewer from './PanoramaViewer';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';


const fade: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.35, ease: 'easeOut' } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.25, ease: 'easeIn' } },
};

const Galerie = () => {
  const { t } = useTranslation();

  const normalPhotos = [
    { image: '/images/gallery-01.webp', descriptionKey: 'gallery.photo1' },
    { image: '/images/gallery-02.webp', descriptionKey: 'gallery.photo2' },
    { image: '/images/gallery-03.webp', descriptionKey: 'gallery.photo3' },
    { image: '/images/gallery-04.webp', descriptionKey: 'gallery.photo4' },
    { image: '/images/gallery-05.webp', descriptionKey: 'gallery.photo5' },
    { image: '/images/gallery-06.webp', descriptionKey: 'gallery.photo6' },
    { image: '/images/gallery-07.webp', descriptionKey: 'gallery.photo7' },
    { image: '/images/gallery-08.webp', descriptionKey: 'gallery.photo8' },
    { image: '/images/gallery-09.webp', descriptionKey: 'gallery.photo9' },
    { image: '/images/gallery-10.webp', descriptionKey: 'gallery.photo10' },
    { image: '/images/gallery-11.webp', descriptionKey: 'gallery.photo11' },
    { image: '/images/gallery-13.webp', descriptionKey: 'gallery.photo12' },
    { image: '/images/gallery-14.webp', descriptionKey: 'gallery.photo13' },
    { image: '/images/gallery-15.webp', descriptionKey: 'gallery.photo14' },
    { image: '/images/gallery-16.webp', descriptionKey: 'gallery.photo15' },
    { image: '/images/gallery-17.webp', descriptionKey: 'gallery.photo16' },
    { image: '/images/gallery-18.webp', descriptionKey: 'gallery.photo17' },
  ];

  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const openModal = (image: string) => {
    setSelectedImage(image);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  // close on ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="min-h-screen bg-[#f7f2ed] text-[#3f2b25]">
      <div className="max-w-6xl mx-auto px-6 py-20 space-y-16">
        {/* Nadpis */}
        <div className="text-center">
          <h1 className="text-4xl font-extrabold mb-2">{t('gallery.title')}</h1>
          <p className="text-base text-gray-600">
            {t('gallery.subtitle')}
          </p>
        </div>

        {/* Běžné fotky */}
        <div>
          <h2 className="text-2xl font-semibold mb-6 text-center text-[#4B2E2E]">
            {t('gallery.sectionHouse')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {normalPhotos.map((photo, index) => (
              <motion.div
                key={index}
                className="relative bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg cursor-pointer group"
                initial="hidden"
                animate="visible"
                variants={fade}
                whileHover={{ scale: 1.02 }}
                onClick={() => openModal(photo.image)}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={photo.image}
                    alt={t(photo.descriptionKey)}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent opacity-0 group-hover:opacity-100 transition" />
                </div>
                <div className="p-5">
                  <p className="text-base leading-relaxed">{t(photo.descriptionKey)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* 360° panoramata */}
        <div>
          <h2 className="text-2xl font-semibold mb-6 text-center text-[#4B2E2E]">
            {t('gallery.section360')}
          </h2>
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <PanoramaViewer />
          </div>
        </div>
      </div>

      {/* Lightbox modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="relative max-w-4xl w-full"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage}
                alt={t('gallery.enlargedPhoto')}
                className="w-full h-auto max-h-[85vh] object-contain rounded-2xl"
              />
              <button
                aria-label={t('common.close')}
                className="absolute top-3 right-3 text-white bg-[#4B2E2E] rounded-full w-10 h-10 flex items-center justify-center hover:bg-[#3a1e1e] transition"
                onClick={closeModal}
              >
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Galerie;
